import { IsoDateStr, Maybe, UnixTimeMillis, ZonedDateTime } from '@csp/csp-common-model';
import { ZonedDateTimeFormatter } from '../util/ZonedDateTimeFormatter';
import { toFormatIsoDateExclusive } from '../util/DateUtil';

export type WindowInfo = WindowInfoDisplayStrings &
  WindowInfoConditions &
  WindowInfoFunctions & {
    windowStart?: ZonedDateTime;
    windowEnd?: ZonedDateTime;
  };

type WindowInfoDisplayStrings = {
  windowStartDateStr: IsoDateStr; // Ex: 2021-01-20 or -
  windowEndDateStr: IsoDateStr; // Ex:  2021-03-20 or -
  windowDateStr: string; // Ex: 2021-01-20 - 2021-03-20 or -
};

type WindowInfoConditions = {
  hasWindow: boolean;
  hasWindowStart: boolean;
  hasWindowEnd: boolean;
  hasWindowStartAndEnd: boolean;
};

type WindowInfoFunctions = {
  isNowWithinWindow(): boolean;
  isNowOutsideWindow(): boolean;
  isNowBeforeWindow(): boolean;
  isNowAfterWindow(): boolean;
  isWithinWindow(timestamp: ZonedDateTime): boolean;
};

const checkIfWindowExist = (windowStart: Maybe<ZonedDateTime>, windowEnd: Maybe<ZonedDateTime>): boolean => {
  if (
    (!windowStart && !windowEnd) ||
    (windowStart && windowEnd && windowStart.unixTimeMillis >= windowEnd.unixTimeMillis)
  ) {
    return false;
  } else {
    return true;
  }
};

const toWindowInfoDisplayStrings = (
  start: Maybe<ZonedDateTime>,
  end: Maybe<ZonedDateTime>,
): WindowInfoDisplayStrings => {
  const windowStartDateStr = start ? ZonedDateTimeFormatter.toIsoDateString(start) : '-';
  const windowEndDateStr = end ? toFormatIsoDateExclusive(end.unixTimeMillis) : '-';
  const windowDateStr = start && end ? `${windowStartDateStr} - ${windowEndDateStr}` : '-';

  return {
    windowStartDateStr,
    windowEndDateStr,
    windowDateStr,
  };
};

const toWindowInfoConditions = (
  windowStart: Maybe<ZonedDateTime>,
  windowEnd: Maybe<ZonedDateTime>,
): WindowInfoConditions => {
  const hasWindow = checkIfWindowExist(windowStart, windowEnd);
  const hasWindowStart = !!windowStart;
  const hasWindowEnd = !!windowEnd;
  const hasWindowStartAndEnd = hasWindowStart && hasWindowEnd;

  return {
    hasWindowStart,
    hasWindowEnd,
    hasWindowStartAndEnd,
    hasWindow,
  };
};

const toWindowValidationFunctions = (
  hasWindow: boolean,
  windowStart: Maybe<ZonedDateTime>,
  windowEnd: Maybe<ZonedDateTime>,
): WindowInfoFunctions => {
  const isNowAfterWindow = (): boolean => hasWindow && (windowEnd ? Date.now() > windowEnd.unixTimeMillis : false);
  const isNowBeforeWindow = (): boolean => hasWindow && (windowStart ? Date.now() < windowStart.unixTimeMillis : false);

  const isWithinWindow = (timestamp: ZonedDateTime | UnixTimeMillis): boolean => {
    const ts = typeof timestamp === 'object' ? timestamp.unixTimeMillis : timestamp;
    if (hasWindow) {
      const startMillis = windowStart ? windowStart.unixTimeMillis : Number.MIN_VALUE;
      const endMillis = windowEnd ? windowEnd.unixTimeMillis : Number.MAX_VALUE;
      return ts >= startMillis && ts < endMillis;
    } else {
      return false;
    }
  };

  const isNowWithinWindow = (): boolean => hasWindow && isWithinWindow(Date.now());
  const isNowOutsideWindow = (): boolean => hasWindow && !isNowWithinWindow();

  return {
    isNowAfterWindow,
    isNowBeforeWindow,
    isWithinWindow,
    isNowWithinWindow,
    isNowOutsideWindow,
  };
};

const from = (windowStart: Maybe<ZonedDateTime>, windowEnd: Maybe<ZonedDateTime>): WindowInfo => {
  const windowInfoConditions = toWindowInfoConditions(windowStart, windowEnd);
  const windowInfoStrings = toWindowInfoDisplayStrings(windowStart, windowEnd);
  const windowValidationFunctions = toWindowValidationFunctions(windowInfoConditions.hasWindow, windowStart, windowEnd);

  return {
    windowStart,
    windowEnd,
    ...windowInfoStrings,
    ...windowValidationFunctions,
    ...windowInfoConditions,
  };
};

export const WindowInfo = {
  from,
};
